import { useEffect, useRef } from 'react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { toSelectedPlace } from '../utils/parsePolishAddress'

const getAutocompleteInput = autocomplete => {
	if (!autocomplete) return null
	return autocomplete.querySelector?.('input')
		|| autocomplete.shadowRoot?.querySelector?.('input')
		|| null
}

const setAutocompleteInputValue = (autocomplete, nextValue, { focus = false } = {}) => {
	const text = `${nextValue || ''}`
	if (!autocomplete) return

	try {
		autocomplete.value = text
	} catch {
		// Some Maps builds expose value only on the inner input.
	}

	const input = getAutocompleteInput(autocomplete)
	if (!input) return

	input.value = text
	if (focus) {
		input.focus()
		input.dispatchEvent(new Event('input', { bubbles: true }))
	}
}

const resolveFirstPlaceFromQuery = async query => {
	const trimmed = `${query || ''}`.trim()
	if (!trimmed || !window.google?.maps) return null

	const placesLibrary = await window.google.maps.importLibrary('places')

	if (placesLibrary?.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
		const { suggestions } = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions({
			input: trimmed,
			includedRegionCodes: ['pl'],
		})
		const placePrediction = suggestions?.[0]?.placePrediction
		if (placePrediction) {
			const place = placePrediction.toPlace()
			await place.fetchFields({
				fields: ['id', 'formattedAddress', 'location', 'addressComponents'],
			})
			return toSelectedPlace(place)
		}
	}

	// Fallback when AutocompleteSuggestion is unavailable.
	const geocoder = new window.google.maps.Geocoder()
	const response = await geocoder.geocode({
		address: trimmed,
		componentRestrictions: { country: 'PL' },
	})
	const firstResult = response?.results?.[0]
	if (!firstResult?.geometry?.location) return null

	return toSelectedPlace({
		id: firstResult.place_id || '',
		formattedAddress: firstResult.formatted_address || trimmed,
		location: firstResult.geometry.location,
		addressComponents: firstResult.address_components,
	})
}

export default function AddressSearchBar({
	placeholder = 'Podaj adres',
	onPlaceSelect,
	searchRequest = null,
	onSearchRequestComplete,
}) {
	const inputRef = useRef(null)
	const autocompleteRef = useRef(null)
	const placesLib = useMapsLibrary('places')
	const onPlaceSelectRef = useRef(onPlaceSelect)
	const onSearchRequestCompleteRef = useRef(onSearchRequestComplete)

	useEffect(() => {
		onPlaceSelectRef.current = onPlaceSelect
	}, [onPlaceSelect])

	useEffect(() => {
		onSearchRequestCompleteRef.current = onSearchRequestComplete
	}, [onSearchRequestComplete])

	useEffect(() => {
		if (!window.google || !inputRef.current || !placesLib) return
		if (!window.google.maps?.places?.PlaceAutocompleteElement) {
			console.error('PlaceAutocompleteElement is unavailable in this Maps JS channel. Try version="beta".')
			return
		}

		const autocomplete = new window.google.maps.places.PlaceAutocompleteElement()
		autocomplete.placeholder = placeholder
		autocomplete.componentRestrictions = { country: ['pl'] }
		autocomplete.types = ['address']
		autocomplete.style.width = '100%'
		autocomplete.style.display = 'block'
		autocompleteRef.current = autocomplete

		inputRef.current.innerHTML = ''
		inputRef.current.appendChild(autocomplete)

		const handlePlaceChange = async ({ placePrediction }) => {
			if (!placePrediction) return

			const place = placePrediction.toPlace()
			await place.fetchFields({
				fields: ['id', 'formattedAddress', 'location', 'addressComponents'],
			})

			if (!place.location) {
				console.error('No geometry returned for selected place')
				return
			}

			const result = toSelectedPlace(place)
			if (result && typeof onPlaceSelectRef.current === 'function') {
				onPlaceSelectRef.current(result)
			}
		}

		autocomplete.addEventListener('gmp-select', handlePlaceChange)

		return () => {
			autocomplete.removeEventListener('gmp-select', handlePlaceChange)
			autocompleteRef.current = null
		}
	}, [placesLib, placeholder])

	useEffect(() => {
		if (!searchRequest?.nonce || !searchRequest?.value) return

		let cancelled = false

		const run = async () => {
			setAutocompleteInputValue(autocompleteRef.current, searchRequest.value, { focus: true })

			try {
				const selectedPlace = await resolveFirstPlaceFromQuery(searchRequest.value)
				if (cancelled) return

				if (selectedPlace) {
					setAutocompleteInputValue(
						autocompleteRef.current,
						selectedPlace.formattedAddress || searchRequest.value,
						{ focus: false }
					)
					onPlaceSelectRef.current?.(selectedPlace)
					onSearchRequestCompleteRef.current?.({ success: true })
					return
				}

				onSearchRequestCompleteRef.current?.({
					success: false,
					error: 'NO_RESULTS',
				})
			} catch (error) {
				console.error('Failed to resolve address from placówka:', error)
				if (!cancelled) {
					onSearchRequestCompleteRef.current?.({
						success: false,
						error: error?.message || 'SEARCH_FAILED',
					})
				}
			}
		}

		run()

		return () => {
			cancelled = true
		}
	}, [searchRequest])

	return (
		<div ref={inputRef} />
	)
}
